import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';

/**
 * NoticeService - Servis za upravljanje objavama (Notices)
 * 
 * Omogućava komunikaciju sa Notice API endpoint-ima:
 * - POST /notice/search - Pretraga aktivnih objava
 * - POST /notice/search-archived - Pretraga arhiviranih objava
 * - POST /notice/save-or-update - Dodavanje/editovanje objave
 * - POST /notice/invalidate - Invalidacija objave
 */
class NoticeService {
    /**
     * Pretražuje aktivne objave
     * @returns {Promise<Array>} Lista aktivnih objava
     */
    async search(signal = null) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.NOTICE.SEARCH, {}, {}, signal);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretražuje jednu objavu po javnom slug-u.
     * BE prihvata isključivo `{ slug }` — za učitavanje po UUID koristi admin fetch (SEO → slug).
     * @param {string} slug - Javni URL slug objave
     * @returns {Promise<Object|null>} Odgovor servera (često { notice: {...} })
     */
    async searchOne(slug, signal = null) {
        try {
            const value = String(slug ?? '').trim();
            const response = await ApiService.post(
                API_ENDPOINTS.NOTICE.SEARCH_ONE,
                { slug: value },
                {},
                signal
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pronalazi objavu koja sadrži dati dokument (npr. za obaveštenja o komentarima).
     * @param {string} documentId - ID dokumenta
     * @returns {Promise<Object>} Odgovor servera (često { notice, seo })
     */
    async searchByDocument(documentId, signal = null) {
        try {
            const response = await ApiService.post(
                API_ENDPOINTS.NOTICE.SEARCH_BY_DOCUMENT,
                { document_id: String(documentId) },
                {},
                signal
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretražuje arhivirane objave
     * @returns {Promise<Array>} Lista arhiviranih objava
     */
    async searchArchived(signal = null) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.NOTICE.SEARCH_ARCHIVED, {}, {}, signal);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Čuva ili ažurira objavu
     * @param {Object} notice - Objava koja se čuva/ažurira
     * @param {string} notice.id - ID objave (opciono za novu objavu)
     * @param {string} notice.title - Naslov objave
     * @param {string} notice.description - Opis/tekst objave
     * @param {string} notice.effective_from - Datum od kada važi (ISO format)
     * @param {string} notice.effective_to - Datum do kada važi (ISO format)
     * @param {string} notice.type - Tip objave (REPORT, itd.)
     * @param {string[]} [notice.consultation_document_ids] - ID-jevi PDF dokumenata za koje su uključene konsultacije (samo za tip CONSULTATION).
     * @param {string[]} [notice.related_document_ids] - ID-jevi priloženih dokumenata; pri verzionisanju objave BE može da prebaci veze na novi red (vidi search / home).
     * @returns {Promise<Object>} Sačuvana objava
     */
    async saveOrUpdate(notice, headers = {}) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.NOTICE.SAVE_OR_UPDATE, notice, headers);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Invalidira (briše) objavu
     * @param {string} id - ID objave koja se invalidira
     * @returns {Promise<Object>} Odgovor servera
     */
    async invalidate(id) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.NOTICE.INVALIDATE, { id });
            return response.data;
        } catch (error) {
            throw error;
        }
    }
}

const noticeServiceInstance = new NoticeService();
export default noticeServiceInstance;
